import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight, Car, CheckCircle, Loader2, Mail, ShieldAlert } from 'lucide-react'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { supabase } from '@/integrations/supabase/client'

export const Route = createFileRoute('/invite/$token')({
  component: InvitePage,
})

function InvitePage() {
  const { token } = Route.useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [inviteDetails, setInviteDetails] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(false)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  useEffect(() => {
    async function checkInvite() {
      try {
        const { data, error: rpcError } = await supabase.rpc('get_invite_details', {
          invite_uuid: token,
        })

        if (rpcError) throw rpcError

        if (data.error) {
          setError(data.error)
        } else if (!data.is_valid) {
          setError('Este convite expirou ou já foi utilizado.')
        } else {
          setInviteDetails(data)
        }
      } catch (_err: any) {
        setError('Ocorreu um erro ao buscar os dados do convite.')
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      checkInvite()
    }
  }, [token])

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)

    try {
      // 1. Criar usuário no Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: name,
            company_id: inviteDetails.company_id,
            role: inviteDetails.role,
            invite_token: token,
          },
        },
      })

      if (authError) throw authError

      // Se o usuário foi criado, ele provavelmente já está logado (sessão local criada).
      // Mas se o sistema exigir confirmação de e-mail, authData.session será null.
      // Assumindo fluxo sem confirmação obrigatória para convites:
      if (!authData.session) {
        // Email confirmation required - show success message
        setEmailSent(true)
        return
      }

      // 2. Resgatar o convite (RPC)
      const { error: redeemError } = await supabase.rpc('redeem_invite', { invite_uuid: token })

      if (redeemError) throw redeemError

      // 3. Atualizar localmente o nome na platform_users (pois a trigger já rodou, mas sem nome)
      await supabase.from('platform_users').update({ name }).eq('id', authData.user?.id)

      // 4. Redirecionar
      navigate({ to: '/app' })
    } catch (err: any) {
      setError(err.message || 'Erro ao realizar o cadastro.')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500" size={48} />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0f172a] flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center text-blue-500 mb-4">
          <Car size={48} />
        </div>
        <h2 className="text-center text-3xl font-extrabold text-white">
          AutoWash <span className="text-blue-500">Pro</span>
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-slate-900 py-8 px-4 shadow-xl border border-slate-800 sm:rounded-2xl sm:px-10">
          {emailSent ? (
            <div className="text-center space-y-4">
              <Mail className="mx-auto text-emerald-400" size={48} />
              <h3 className="text-xl font-bold text-slate-200">Cadastro Realizado!</h3>
              <p className="text-slate-400">
                Enviamos um link de confirmação para o seu e-mail. Clique nele para ativar sua conta
                e acessar o sistema.
              </p>
              <Button
                variant="outline"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => navigate({ to: '/login' })}
              >
                Ir para o Login
              </Button>
            </div>
          ) : error ? (
            <div className="text-center space-y-4">
              <ShieldAlert className="mx-auto text-red-500" size={48} />
              <h3 className="text-xl font-bold text-slate-200">Convite Inválido</h3>
              <p className="text-slate-400">{error}</p>
              <Button
                variant="outline"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={() => navigate({ to: '/login' })}
              >
                Voltar para o Login
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center p-2 bg-emerald-500/10 text-emerald-400 rounded-full mb-3">
                  <CheckCircle size={24} />
                </div>
                <h3 className="text-lg font-medium text-slate-200">
                  Você foi convidado para a equipe da <br />
                  <span className="font-bold text-blue-400">{inviteDetails?.company_name}</span>
                </h3>
                <p className="text-sm text-slate-400 mt-2">
                  Cargo atribuído: <strong className="text-white">{inviteDetails?.role}</strong>
                </p>
              </div>

              <form className="space-y-4" onSubmit={handleSignup}>
                <div>
                  <label className="block text-sm font-medium text-slate-300">
                    Seu Nome Completo
                  </label>
                  <div className="mt-1">
                    <Input
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                      placeholder="Ex: Carlos Silva"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300">
                    Email de Acesso
                  </label>
                  <div className="mt-1">
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300">Senha Segura</label>
                  <div className="mt-1">
                    <Input
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-slate-800 border-slate-700 text-slate-100"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 font-bold gap-2 text-md h-12"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <Loader2 className="animate-spin" size={20} />
                    ) : (
                      <>
                        Criar Conta e Acessar <ArrowRight size={20} />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
